import { FC, Fragment, useContext, useMemo } from 'react';
import { FullSegment } from 'ts/shared/interfaces/MOResponse';
import { getCurrency } from 'utils/utils';
import { useTranslation } from 'react-i18next';
import withErrorBoundary from '../withErrorBoundary';
import { MainContext } from 'contexts/MainContext';
import MoGroupData from './MoGroupData';
import { Column } from 'ts/shared/interfaces/Generic';

interface MoAmountProps {
  segment: FullSegment;
}

const SPACER_COLUMN: Column = { label: '\u00A0', field: 'spacer' };

const MoAmount: FC<MoAmountProps> = ({ segment }) => {
  const [t] = useTranslation();
  const { user } = useContext(MainContext);
  const showLevelTwo = !!user?.branch?.orderNetDisplayLevelTwo;
  const showLevelOne = !!user?.branch?.orderNetDisplayLevelOne;
  const currency = getCurrency(segment.sysCurrencyCode);
  const localCurrency = getCurrency('loc');

  const leftColumns: Column[] = useMemo(() => {
    const rows: Column[] = [
      { label: t('mo.gross'), field: 'gross' },
      { label: t('mo.coupon_code'), field: 'couponCode' },
    ];
    if (showLevelTwo) rows.push({ label: t('mo.total_gross'), field: 'totalGross' });
    if (showLevelOne) rows.push({ label: t('mo.total_net'), field: 'totalNet' });
    return rows;
  }, [t, showLevelOne, showLevelTwo]);

  const middleColumns: Column[] = useMemo(() => {
    const rows: Column[] = [
      { label: t('mo.client_id'), field: 'clientId' },
      { label: t('mo.discount'), field: 'discount' },
    ];
    if (showLevelTwo) rows.push(SPACER_COLUMN);
    if (showLevelOne) rows.push(SPACER_COLUMN);
    return rows;
  }, [t, showLevelOne, showLevelTwo]);

  const rightColumns: Column[] = useMemo(() => {
    const rows: Column[] = [
      { label: `${t('mo.gross')} ${t('mo.local')}`, field: 'grossLocal' },
      SPACER_COLUMN,
    ];
    if (showLevelTwo) {
      rows.push({ label: `${t('mo.total_gross')} ${t('mo.local')}`, field: 'totalGrossLocal' });
    }
    if (showLevelOne) {
      rows.push({ label: `${t('mo.total_net')} ${t('mo.local')}`, field: 'totalNetLocal' });
    }
    return rows;
  }, [t, showLevelOne, showLevelTwo]);

  const data = useMemo(
    () => ({
      spacer: '',
      gross: `${currency} ${segment.sysTotalGross2?.toFixed(3)}`,
      clientId: segment.clientId,
      grossLocal: `${localCurrency} ${segment.locTotalGross?.toFixed(3)}`,
      couponCode: segment.couponCode,
      discount: `${currency} ${segment.discountAmount?.toFixed(3)}`,
      totalGross: `${currency} ${segment.sysTotalGross?.toFixed(3)}`,
      totalGrossLocal: `${localCurrency} ${segment.locTotalGross?.toFixed(3)}`,
      totalNet: `${currency} ${segment.sysTotalNett?.toFixed(3)}`,
      totalNetLocal: `${localCurrency} ${segment.locTotalNet?.toFixed(3)}`,
    }),
    [currency, localCurrency, segment]
  );

  return (
    <Fragment>
      <div className="mb-10">
        <span>{t('mo.amount')} </span>
      </div>
      <div className="mb-20 mo-amount-cols display-flex flex-gap-10">
        <MoGroupData
          columns={leftColumns}
          data={data}
          columnsCount={1}
          columnWidth={130}
          mainClassName="mo-group-wr-1x"
        />
        <MoGroupData
          columns={middleColumns}
          data={data}
          columnsCount={1}
          columnWidth={130}
          mainClassName="mo-group-wr-1x"
        />
        <MoGroupData
          columns={rightColumns}
          data={data}
          columnsCount={1}
          columnWidth={130}
          mainClassName="mo-group-wr-1x"
        />
      </div>
    </Fragment>
  );
};

export default withErrorBoundary(MoAmount, 'MoAmount');
